/**
 * Is this page running inside the Matix POS phone app?
 *
 * The app (apps/pos-mobile) is a Capacitor shell that loads the live cashier,
 * so the SAME bundle runs in a browser and in the app. Inside the app the
 * native bridge puts `window.Capacitor` on the page; in a browser it is absent
 * and every function here answers "web" — web code works unchanged.
 *
 * The shell's version travels in the user agent (`MatixPOSShell/<version>`,
 * set by capacitor.config.ts `appendUserAgent`), so it is known synchronously
 * at first paint, before any plugin call could answer.
 */
type CapacitorGlobal = {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
};

function cap(): CapacitorGlobal | undefined {
  return typeof window === 'undefined'
    ? undefined
    : (window as unknown as { Capacitor?: CapacitorGlobal }).Capacitor;
}

export type NativePlatform = 'ios' | 'android' | 'web';

export function isNativeApp(): boolean {
  return Boolean(cap()?.isNativePlatform?.());
}

export function nativePlatform(): NativePlatform {
  if (!isNativeApp()) return 'web';
  const p = cap()?.getPlatform?.();
  return p === 'ios' || p === 'android' ? p : 'web';
}

export const SHELL_UA_TOKEN = 'MatixPOSShell';

/** The installed shell's version ("1.0.0"), or null in a browser. */
export function nativeShellVersion(): string | null {
  if (typeof navigator === 'undefined') return null;
  const m = new RegExp(`${SHELL_UA_TOKEN}/(\\d+(?:\\.\\d+){0,2})`).exec(navigator.userAgent);
  return m ? m[1]! : null;
}

/**
 * True inside ANY installed Matix POS shell — the Capacitor phone apps AND the
 * Windows desktop shell.
 *
 * ⚠ SEPARATE FROM `isNativeApp()` ON PURPOSE, and the separation IS the point.
 * `isNativeApp()` answers «is a Capacitor bridge on this page», and that is what
 * gates biometrics, secure storage, the splash screen, the status bar and the
 * Android back button — every one of those is a Capacitor PLUGIN. The Windows
 * shell is a plain WebView2 window with no bridge and none of those plugins, so
 * widening `isNativeApp()` would switch all of it on over a bridge that is not
 * there. Checked before writing this: `isNativeApp()` has five consumers and
 * only one of them is the update notice.
 *
 * This asks the narrower question the update notice actually needs — «is this
 * page inside an installed app of ours, of any kind» — and answers it from the
 * user-agent token every shell sets (`MatixPOSShell/<version>`), which needs no
 * native code at all. The Capacitor check stays in the OR so a shell that ever
 * ships without `appendUserAgent` still answers correctly.
 */
export function isInstalledShell(): boolean {
  return isNativeApp() || nativeShellVersion() !== null;
}

/** -1 / 0 / 1 for dotted versions; a missing part counts as 0. */
export function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d) return d > 0 ? 1 : -1;
  }
  return 0;
}

/**
 * The oldest shell the cashier still supports. Raise it when the cashier starts
 * relying on something a shell must provide (a plugin, a config key); an older
 * shell then shows the update notice instead of breaking.
 */
export const MIN_NATIVE_SHELL_VERSION = '1.0.0';

/**
 * Where an "update the app" prompt sends the user.
 * ⚠ PLACEHOLDERS until the app is listed: replace with the real store URLs.
 */
export const NATIVE_STORE_URLS: Record<'ios' | 'android', string> = {
  ios: 'https://apps.apple.com/app/matix-pos/id0000000000',
  android: 'https://play.google.com/store/apps/details?id=com.Sismatix.Pos',
};

/** True when the installed shell is older than MIN_NATIVE_SHELL_VERSION. */
export function nativeShellOutdated(min: string = MIN_NATIVE_SHELL_VERSION): boolean {
  const v = nativeShellVersion();
  return isNativeApp() && v !== null && compareVersions(v, min) < 0;
}

/**
 * App Store rule (3.1.1): an iOS app may not offer, or point to, a way to buy
 * the app's own service outside Apple's in-app purchase. The SHOP's own sales
 * to its customers are the merchant's business and are not affected — this only
 * covers a Matix subscription / renewal control, should one ever be added here.
 */
export function hideMatixPurchases(): boolean {
  return nativePlatform() === 'ios';
}

/* ── «فيه تحديث» ─────────────────────────────────────────────────────────────
 *
 * Two things can notice that a newer deploy is published, and the till needs
 * both:
 *
 *   `shell` — the iOS app polls `/version.json` natively and hands the answer
 *             over. It also drives its own strip, so it works on a page that
 *             has not booted.
 *   `web`   — this module polls the same file from the page. ⚠ WITHOUT IT THE
 *             BUTTON WOULD ONLY EVER APPEAR ON iOS: the Android shell carries
 *             no equivalent Swift, so the one place the answer can come from
 *             on every platform is the page itself.
 *
 * ⚠ THE ANSWER IS THE **OR** OF THE SOURCES, NOT THE LAST ONE TO SPEAK. The
 * two see different things — the shell compares against the version the app
 * was launched on, the page against the version it was served. So the page can
 * honestly believe it is current (it was served after the deploy) while the
 * shell is right that the app is behind. Letting whichever reported last win
 * means a correct notice gets switched off by a correct non-notice, and the
 * button flickers out a minute after appearing.
 *
 * ⚠ AND THE STATE IS LATCHED ON `window`, not only dispatched. The shell's
 * first notice can land before React has mounted the sidebar, and an event
 * fired then is gone — the button would stay missing until the next poll a
 * minute later, which reads as the feature not working.
 */
export const UPDATE_AVAILABLE_EVENT = 'matix:update-available';

export type UpdateSource = 'shell' | 'web';

type UpdateFlagWindow = { __matixUpdateAvailable?: boolean };

const reported: Record<UpdateSource, boolean> = { shell: false, web: false };

function flagWindow(): UpdateFlagWindow | undefined {
  return typeof window === 'undefined' ? undefined : (window as unknown as UpdateFlagWindow);
}

/** Is a newer deploy waiting? False in a browser — it has its own reload. */
export function readUpdateAvailable(): boolean {
  return flagWindow()?.__matixUpdateAvailable === true;
}

/**
 * Record what one source sees and publish the combined answer. Idempotent: a
 * source repeating itself dispatches nothing, so a 60 s poll is silent until
 * something actually changes.
 */
export function reportUpdateAvailable(source: UpdateSource, available: boolean): void {
  const w = flagWindow();
  if (!w) return;
  if (reported[source] === available && w.__matixUpdateAvailable !== undefined) return;
  reported[source] = available;
  const combined = reported.shell || reported.web;
  if (w.__matixUpdateAvailable === combined) return;
  w.__matixUpdateAvailable = combined;
  window.dispatchEvent(new CustomEvent(UPDATE_AVAILABLE_EVENT, { detail: combined }));
}

/** Calls `cb` on every change. Returns the unsubscribe. */
export function subscribeUpdateAvailable(cb: (available: boolean) => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const handler = (e: Event) => cb((e as CustomEvent<unknown>).detail === true);
  window.addEventListener(UPDATE_AVAILABLE_EVENT, handler);
  return () => window.removeEventListener(UPDATE_AVAILABLE_EVENT, handler);
}

/**
 * The door the iOS shell knocks on. It cannot import anything, so the registry
 * has to be reachable by name from `evaluateJavaScript`; the shell falls back
 * to writing the raw flag if this is not installed yet.
 */
export function installShellUpdateBridge(): void {
  const w = flagWindow() as (UpdateFlagWindow & {
    __matixReportUpdate?: (s: UpdateSource, v: unknown) => void;
  }) | undefined;
  if (!w) return;
  // Adopt anything the shell wrote before the bundle was ready.
  if (w.__matixUpdateAvailable === true) reported.shell = true;
  w.__matixReportUpdate = (source, value) => {
    if (source !== 'shell' && source !== 'web') return;
    reportUpdateAvailable(source, value === true);
  };
}

const VERSION_PATH = '/version.json';
const UPDATE_POLL_MS = 60_000;

async function readPublishedVersion(): Promise<string | null> {
  try {
    // ⚠ BOTH the cache-buster and `no-store`. A cached `version.json` answers
    // with the deploy this page was served from, forever — the poll then runs
    // every minute and can never once report an update. Same pair the ERP's
    // own strip uses.
    const res = await fetch(`${VERSION_PATH}?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const body: unknown = await res.json();
    if (!body || typeof body !== 'object') return null;
    const commit = (body as { commit?: unknown }).commit;
    return typeof commit === 'string' && commit.length > 0 ? commit : null;
  } catch {
    return null;
  }
}

/**
 * Watch `/version.json` for a deploy newer than the one this page is running.
 *
 * ⚠ «WHAT THIS PAGE IS RUNNING» IS THE FIRST READING, NOT A BUILD STAMP. The
 * POS bundle carries no build id, and rather than add one to the build this
 * takes the same approach the shell does: the page was served by the server a
 * moment ago, so the first successful read IS this page's version. Every later
 * reading that differs is a deploy that landed since.
 *
 * ⚠ The one case it cannot see: a deploy that lands between the HTML being
 * served and this first read — a window of a second or two — is adopted as
 * «me», and that deploy goes unnoticed until the next one. The shell has the
 * same property. Not worth a build stamp to close; worth knowing about.
 *
 * Paused while the tab is hidden and re-checked the moment it comes back: a
 * till on a tablet sleeps for hours, and a cashier picking it up should not
 * wait out an interval to be told.
 */
export function startUpdateWatch(pollMs: number = UPDATE_POLL_MS): () => void {
  if (typeof window === 'undefined') return () => {};
  let own: string | null = null;
  let cancelled = false;

  const check = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    void readPublishedVersion().then((published) => {
      if (cancelled || published === null) return;
      if (own === null) {
        own = published;
        return;
      }
      reportUpdateAvailable('web', published !== own);
    });
  };

  check();
  const timer = window.setInterval(check, pollMs);
  const onVisible = () => {
    if (document.visibilityState === 'visible') check();
  };
  window.addEventListener('focus', check);
  document.addEventListener('visibilitychange', onVisible);
  return () => {
    cancelled = true;
    window.clearInterval(timer);
    window.removeEventListener('focus', check);
    document.removeEventListener('visibilitychange', onVisible);
  };
}
